import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { raiseToast } from '$components/Toast';
import Modal from '$components/Modal';
import TextField from '$components/TextField';
import Select from '$components/Select';
import hasError from '$utils/hasError';
import { gradeTypeTitles, gradeTypes, providers } from '$variables/constants';
import { Assignment, GradeType } from '$variables/types';
import validateAssignment from './utils/validateAssignment';
import hasAnyGrade from '$utils/hasAnyGrade';
import { Header, Content, Subtitle } from './ManualAssignmentModal.styled';

const gradeTypeOptions = [
  {
    name: gradeTypeTitles[gradeTypes.freeform],
    value: gradeTypes.freeform,
  },
  {
    name: gradeTypeTitles[gradeTypes.passFail],
    value: gradeTypes.passFail,
  },
  {
    name: gradeTypeTitles[gradeTypes.percentage],
    value: gradeTypes.percentage,
  },
];

const ManualAssignmentModal = ({
  open,
  onClose,
  onSave,
  isSaving,
  spaceId,
  initialAssignment,
  grades,
}) => {
  const [assignment, setAssignment] = useState(
    initialAssignment || {
      title: '',
      gradeType: gradeTypes.percentage,
      space: spaceId,
      provider: providers.MANUAL,
    }
  );

  const errors = validateAssignment(assignment);

  const handleSave = async () => {
    try {
      await onSave({ assignment });
      raiseToast({ message: 'Assignment saved!' });
      onClose();
    } catch (_) {
      raiseToast({ message: 'Oops! Something went wrong.', type: 'fail' });
    }
  };

  const handleChangeTitle = ({ target: { value } }) => {
    setAssignment({
      ...assignment,
      title: value,
    });
  };

  const handleChangeGradeType = (_, selectedGradeType) => {
    setAssignment({
      ...assignment,
      gradeType: selectedGradeType,
    });
  };

  const [titleTouched, setTitleTouched] = useState(false);
  const handleBlurTitle = () => {
    setTitleTouched(true);
  };

  const isEditing = !!initialAssignment;
  const headerText = isEditing
    ? 'Update manual assignment'
    : 'Add manual assignment';
  const submitText = isEditing ? 'Update assignment' : 'Create assignment';
  const hasGrades = grades && hasAnyGrade(grades);

  return (
    <Modal
      headerText={headerText}
      open={open}
      handleClose={onClose}
      submitText={submitText}
      isLoading={isSaving}
      isDisabled={hasError(errors)}
      handleSubmit={handleSave}
    >
      <Header>
        <TextField
          value={assignment.title}
          onChange={handleChangeTitle}
          onBlur={handleBlurTitle}
          label="Assignment title"
          fullWidth
          error={titleTouched && errors.title}
        />
      </Header>
      <Content>
        <Subtitle>Grade type</Subtitle>
        <Select
          options={gradeTypeOptions}
          value={assignment.gradeType}
          placeholder="Select a grade type"
          onChange={handleChangeGradeType}
          disabled={isEditing && hasGrades}
        />
      </Content>
    </Modal>
  );
};

ManualAssignmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
  spaceId: PropTypes.string,
  initialAssignment: Assignment,
  grades: PropTypes.arrayOf(GradeType),
};

ManualAssignmentModal.defaultProps = {
  isSaving: false,
  spaceId: undefined,
  initialAssignment: undefined,
  grades: [],
};

export default ManualAssignmentModal;
